import type { MentionCandidate } from '@hellodarwin/app-features/lib/features/conversations/types';
import type { AssetWithOwnersEntity } from '@hellodarwin/core/lib/features/entities/assets-entities';
import { useCallback, useState } from 'react';
import { useAppDispatch } from '../../../../../app/app-hooks';
import { RequiredDocuments } from '../../../../../components/program-documents/required-documents';
import { ProgramDocumentUploadDrawer } from '../../../../../components/program-documents/upload-drawer';
import { WorkingDocuments } from '../../../../../components/program-documents/working-documents';
import { ProgramTasks } from '../../../../../components/program-tasks';
import type { ProgramDocumentRequirement } from '../../../../../features/api/program-documents-api';
import { downloadAsset } from '../../../../../features/api/slices/assets-slice';
import { useNewClientApi } from '../../../../../features/api/use-client-api';
import { useProgramDocuments } from '../../../../../hooks/use-program-documents';
import { useProgramTasks } from '../../../../../hooks/use-program-tasks';
import ClientConversationBlock from '../client-conversation-block';
import { ConversationSection, ManagedWorkspace, WorkspacePanel } from './styles';

interface ManagedProgramWorkspaceProps {
  programId: string;
  mentionCandidates?: MentionCandidate[];
}

export const ManagedProgramWorkspace = ({
  programId,
  mentionCandidates = [],
}: ManagedProgramWorkspaceProps) => {
  const dispatch = useAppDispatch();
  const api = useNewClientApi();
  const documents = useProgramDocuments(programId);
  const tasks = useProgramTasks(programId);
  const refreshProgramDocuments = documents.refresh;
  const refreshProgramTasks = tasks.refresh;
  const isInitialDocumentsLoading =
    !documents.data &&
    (documents.status === 'idle' || documents.status === 'pending');
  const [selectedRequirement, setSelectedRequirement] =
    useState<ProgramDocumentRequirement>();

  const retryDocuments = useCallback(() => {
    void refreshProgramDocuments().catch(() => undefined);
  }, [refreshProgramDocuments]);
  const retryTasks = useCallback(() => {
    void refreshProgramTasks().catch(() => undefined);
  }, [refreshProgramTasks]);
  const handleDownload = useCallback(
    async (asset: AssetWithOwnersEntity) => {
      try {
        const response = await dispatch(
          downloadAsset({ api, asset_id: asset.asset_id }),
        ).unwrap();
        window.open(response.link, 'download');
      } catch (error) {
        console.error(error);
      }
    },
    [api, dispatch],
  );

  return (
    <ManagedWorkspace>
      <WorkspacePanel>
        <ProgramTasks
          tasks={tasks.tasks}
          status={tasks.status}
          mutationStatusByTaskId={tasks.mutationStatusByTaskId}
          counts={tasks.counts}
          filter={tasks.filter}
          page={tasks.page}
          pageSize={tasks.pageSize}
          total={tasks.total}
          onFilterChange={tasks.setFilter}
          onPageChange={tasks.setPage}
          onRetry={retryTasks}
          onSetCompleted={tasks.setCompleted}
        />
      </WorkspacePanel>
      <WorkspacePanel>
        <RequiredDocuments
          requirements={documents.data?.required_documents ?? []}
          isLoading={isInitialDocumentsLoading}
          isError={documents.status === 'failed'}
          onRetry={retryDocuments}
          onUpload={setSelectedRequirement}
          onDownload={(asset) => void handleDownload(asset)}
        />
      </WorkspacePanel>
      <WorkspacePanel>
        <WorkingDocuments
          documents={documents.data?.working_documents ?? []}
          isLoading={isInitialDocumentsLoading}
          isError={
            documents.status === 'failed' ||
            documents.data?.working_documents_available === false
          }
          onRetry={retryDocuments}
        />
      </WorkspacePanel>
      <ConversationSection id="application-conversation">
        <ClientConversationBlock
          programId={programId}
          mentionCandidates={mentionCandidates}
        />
      </ConversationSection>
      {selectedRequirement ? (
        <ProgramDocumentUploadDrawer
          key={selectedRequirement.id}
          requirement={selectedRequirement}
          onClose={() => setSelectedRequirement(undefined)}
          onSuccess={retryDocuments}
        />
      ) : null}
    </ManagedWorkspace>
  );
};
